/**
 * QuantumPropertyManager — manages quantum property lifecycles.
 *
 * Handles the common pattern of acquiring, pooling, and releasing WASM
 * QuantumProperty handles. Games either extend this class or compose it
 * to add game-specific quantum operations via getModule().
 *
 * Property pooling is critical: measured/removed properties are recycled
 * to avoid growing the tensor product and hitting qudit limits.
 *
 * For opt-in operation recording, attach a QuantumRecorder via setRecorder().
 */

import { getModule } from "./QuantumForgeLoader";
import type { LoggerInterface } from "../logging/Logger";
import type { QuantumProperty as QFProperty } from "./quantum-forge-api.mjs";

export interface PredicateSpec {
  property: QFProperty;
  value: number;
  isEqual: boolean;
}

export interface QuantumRecorderHook {
  onAcquire?(prop: QFProperty): void;
  onRelease?(prop: QFProperty, value: number): void;
  onSetProperty?(id: string, prop: QFProperty): void;
  onDeleteProperty?(id: string): void;
}

export class QuantumPropertyManager {
  readonly dimension: number;
  private properties: Map<string, QFProperty> = new Map();
  private pool: QFProperty[] = [];
  protected logger?: LoggerInterface;
  private _recorder?: QuantumRecorderHook;

  constructor(options: { dimension?: number; logger?: LoggerInterface } = {}) {
    this.dimension = options.dimension ?? 2;
    this.logger = options.logger;
  }

  // -- Recorder hook --

  /** Attach an optional recorder for operation logging. */
  setRecorder(recorder: QuantumRecorderHook | undefined): void {
    this._recorder = recorder;
  }

  /** Get the currently attached recorder, if any. */
  getRecorder(): QuantumRecorderHook | undefined {
    return this._recorder;
  }

  // -- Property lifecycle --

  /**
   * Get a property at |0⟩ — reuses a pooled one if available,
   * otherwise creates a fresh standalone property.
   */
  acquireProperty(): QFProperty {
    let prop: QFProperty;
    if (this.pool.length > 0) {
      prop = this.pool.pop()!;
    } else {
      prop = getModule().QuantumForge.createQuantumProperty(this.dimension);
    }
    this._recorder?.onAcquire?.(prop);
    return prop;
  }

  /**
   * Return a property to the pool after resetting it to |0⟩.
   * Uses the `reset` primitive which applies non-fractional cycles —
   * correct for all dimensions (no superposition created).
   */
  releaseProperty(prop: QFProperty, measuredValue: number): void {
    this._recorder?.onRelease?.(prop, measuredValue);
    getModule().reset(prop, measuredValue);
    this.pool.push(prop);
  }

  // -- ID mapping --

  setProperty(id: string, prop: QFProperty): void {
    this._recorder?.onSetProperty?.(id, prop);
    this.properties.set(id, prop);
  }

  getProperty(id: string): QFProperty | undefined {
    return this.properties.get(id);
  }

  deleteProperty(id: string): void {
    this._recorder?.onDeleteProperty?.(id);
    this.properties.delete(id);
  }

  hasProperty(id: string): boolean {
    return this.properties.has(id);
  }

  // -- Public operations --

  /**
   * Remove a property by ID: measure it, pool the handle, delete the mapping.
   */
  removeProperty(id: string): void {
    const prop = this.properties.get(id);
    if (prop) {
      const [value] = getModule().measure_properties([prop]);
      this.releaseProperty(prop, value);
    }
    this.deleteProperty(id);
  }

  /** Clear all properties, pool, and recorder. */
  clear(): void {
    this.properties.clear();
    this.pool = [];
  }

  get size(): number {
    return this.properties.size;
  }

  get poolSize(): number {
    return this.pool.length;
  }

  // -- WASM module access --

  getModule(): ReturnType<typeof getModule> {
    return getModule();
  }

  // -- Internal access for QuantumRecorder replay --

  /** @internal — used by QuantumRecorder.replayLog() to restore pool state. */
  _setPool(pool: QFProperty[]): void {
    this.pool = pool;
  }

  /** @internal — used by QuantumRecorder to enumerate live handles. */
  _getProperties(): Map<string, QFProperty> {
    return this.properties;
  }

  /** @internal — used by QuantumRecorder to enumerate pool handles. */
  _getPool(): QFProperty[] {
    return this.pool;
  }
}
